// Updated Brands.jsx with validation and improved error handling
import { useEffect, useState } from "react";
import { Search, Plus, Pencil, Trash2, X } from "lucide-react";
import api from "../../api/axios";
import { useToast } from "../../context/ToastContext";

const empty = {
    brand_id: null,
    brand_name: "",
    brand_description: "",
    brand_website: "",
    brand_logo: "",
    brand_colors: ["#2563eb"],
    brand_status: "ACTIVE",
};

export default function BrandsComponent() {
    const [brands, setBrands] = useState([]);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(false);
    const [form, setForm] = useState(empty);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const { success, error } = useToast();

    // Fetch brands
    const getBrands = async () => {
        try {
            setLoading(true);
            const response = await api.get("brand/list");
            if (response?.data?.success) {
                setBrands(response.data?.data?.list || []);
            } else {
                setBrands([]);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getBrands();
    }, []);

    // Filter brands
    const filtered = brands.filter((b) =>
        b.brand_name.toLowerCase().includes(search.toLowerCase())
    );

    // Form field handlers
    const input = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        // Clear field error on change
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: "" });
        }
    };

    const logo = (e) => {
        const f = e.target.files?.[0];
        if (f) {
            setForm({ ...form, brand_logo: URL.createObjectURL(f) });
            if (errors.brand_logo) {
                setErrors({ ...errors, brand_logo: "" });
            }
        }
    };

    // Validation
    const validate = () => {
        const newErrors = {};
        // Brand name: required, min 2 chars
        if (!form.brand_name || form.brand_name.trim().length < 2) {
            newErrors.brand_name = "Brand name is required (minimum 2 characters)";
        }
        // Website: optional, but if provided must be a valid URL
        if (form.brand_website && !isValidURL(form.brand_website)) {
            newErrors.brand_website = "Please enter a valid URL (e.g., https://example.com)";
        }
        // Colors: at least one
        if (!form.brand_colors || form.brand_colors.length === 0) {
            newErrors.brand_colors = "At least one color is required";
        }
        // Status: required
        if (!form.brand_status) {
            newErrors.brand_status = "Status is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isValidURL = (string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    // Create brand
    const createBrand = async () => {
        if (!validate()) return;
        try {
            const payload = {
                brand_name: form.brand_name,
                brand_description: form.brand_description,
                brand_website: form.brand_website,
                brand_logo: form.brand_logo,
                brand_colors: form.brand_colors,
                brand_status: form.brand_status,
            };
            await api.post("/brand/save", payload);
            success("Brand created successfully");
            getBrands();
            closeModal();
        } catch (err) {
            console.log(err);
            error(err?.data?.message || "Failed to create brand");
        }
    };

    // Update brand
    const updateBrand = async () => {
        if (!validate()) return;
        try {
            const payload = {
                brand_name: form.brand_name,
                brand_description: form.brand_description,
                brand_website: form.brand_website,
                brand_logo: form.brand_logo,
                brand_colors: form.brand_colors,
                brand_status: form.brand_status,
            };
            await api.put(`/brand/${form.brand_id}`, payload);
            success("Brand updated successfully");
            getBrands();
            closeModal();
        } catch (err) {
            console.log(err);
            error(err?.response?.data?.message || "Failed to update brand");
        }
    };

    // Delete brand (optional – implement if API endpoint exists)
    const deleteBrand = async (brandId) => {
        if (!window.confirm("Are you sure you want to delete this brand?")) return;
        try {
            await api.delete(`/brand/${brandId}`);
            success("Brand deleted");
            getBrands();
        } catch (err) {
            error(err?.response?.data?.message || "Failed to delete brand");
        }
    };

    // Open modal for edit
    const handleEdit = (brand) => {
        setEdit(true);
        setForm({
            brand_id: brand.brand_id,
            brand_name: brand.brand_name,
            brand_description: brand.brand_description,
            brand_website: brand.brand_website,
            brand_logo: brand.brand_logo,
            brand_colors: brand.brand_colors,
            brand_status: brand.brand_status,
        });
        setErrors({});
        setOpen(true);
    };

    // Open modal for create
    const handleCreate = () => {
        setEdit(false);
        setForm(empty);
        setErrors({});
        setOpen(true);
    };

    // Close modal
    const closeModal = () => {
        setOpen(false);
        setForm(empty);
        setErrors({});
        setEdit(false);
    };

    return (
        <div className="p-6 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            Brand Management
                        </h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Create, organize, and manage all your organization brands from one place.
                        </p>
                    </div>
                    <button
                        onClick={handleCreate}
                        className="flex items-center gap-2 rounded-xl bg-blue-600 dashboard-btn-new px-5 py-3 font-medium text-white shadow-sm transition hover:bg-blue-700"
                    >
                        <Plus size={18} />
                        Create Brand
                    </button>
                </div>

                {/* Search */}
                <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="relative w-full">
                        <Search
                            size={18}
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by brand name or website..."
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-11 pr-4 text-sm focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl shadow overflow-hidden">
                    <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
                        <table className="min-w-full">
                            <thead className="border-b bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        Brand
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        Website
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        Colors
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        Status
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filtered.map((brand) => (
                                    <tr key={brand.brand_id} className="transition hover:bg-gray-50">
                                        {/* Brand */}
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-4">
                                                {brand.brand_logo && (
                                                    <img
                                                        src={brand.brand_logo}
                                                        alt={brand.brand_name}
                                                        className="h-14 w-14 rounded-xl border border-gray-200 object-cover"
                                                    />
                                                )}
                                                <div>
                                                    <h4 className="font-semibold text-gray-900">
                                                        {brand.brand_name}
                                                    </h4>
                                                    <p className="mt-1 max-w-xs text-sm text-gray-500 line-clamp-2">
                                                        {brand.brand_description}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        {/* Website */}
                                        <td className="px-6 py-5">
                                            <a
                                                href={brand.brand_website}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {brand.brand_website}
                                            </a>
                                        </td>
                                        {/* Colors */}
                                        <td className="px-6 py-5">
                                            <div className="flex justify-center gap-2">
                                                {brand.brand_colors.map((color, index) => (
                                                    <div
                                                        key={index}
                                                        className="h-8 w-8 rounded-full border-2 border-white shadow"
                                                        style={{ backgroundColor: color }}
                                                        title={color}
                                                    />
                                                ))}
                                            </div>
                                        </td>
                                        {/* Status */}
                                        <td className="px-6 py-5 text-center">
                                            <span
                                                className={`inline-flex rounded-full px-4 py-1 text-xs font-semibold ${
                                                    brand.brand_status === "ACTIVE"
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-red-100 text-red-600"
                                                }`}
                                            >
                                                {brand.brand_status}
                                            </span>
                                        </td>
                                        {/* Actions */}
                                        <td className="px-6 py-5">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => handleEdit(brand)}
                                                    className="rounded-lg border border-amber-200 p-2 text-amber-600 transition hover:bg-amber-50"
                                                >
                                                    <Pencil size={18} />
                                                </button>
                                                <button
                                                    onClick={() => deleteBrand(brand.brand_id)}
                                                    className="rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="py-12 text-center text-gray-500">
                                            No brands found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Modal */}
                {open && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center border-b p-6">
                                <div>
                                    <h2 className="text-2xl font-bold">
                                        {edit ? "Edit" : "Create"} Brand
                                    </h2>
                                    <p className="text-gray-500 text-sm">Brand Information</p>
                                </div>
                                <button onClick={closeModal} className="hover:bg-gray-100 p-1 rounded">
                                    <X size={24} />
                                </button>
                            </div>
                            <div className="p-6 grid grid-cols-2 gap-5">
                                {/* Brand Name */}
                                <div className="col-span-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        Brand Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        name="brand_name"
                                        value={form.brand_name}
                                        onChange={input}
                                        className={`w-full border rounded-lg p-2 mt-1 ${
                                            errors.brand_name ? "border-red-500" : "border-gray-300"
                                        }`}
                                    />
                                    {errors.brand_name && (
                                        <p className="text-red-500 text-sm mt-1">{errors.brand_name}</p>
                                    )}
                                </div>

                                {/* Website */}
                                <div className="col-span-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        Website (optional)
                                    </label>
                                    <input
                                        name="brand_website"
                                        value={form.brand_website}
                                        onChange={input}
                                        className={`w-full border rounded-lg p-2 mt-1 ${
                                            errors.brand_website ? "border-red-500" : "border-gray-300"
                                        }`}
                                        placeholder="https://example.com"
                                    />
                                    {errors.brand_website && (
                                        <p className="text-red-500 text-sm mt-1">{errors.brand_website}</p>
                                    )}
                                </div>

                                {/* Description */}
                                <div className="col-span-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        Description (optional)
                                    </label>
                                    <textarea
                                        name="brand_description"
                                        value={form.brand_description}
                                        onChange={input}
                                        rows={3}
                                        className="w-full border border-gray-300 rounded-lg p-2 mt-1"
                                    />
                                </div>

                                {/* Colors */}
                                <div className="col-span-2">
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="text-sm font-medium text-gray-700">
                                            Brand Colors <span className="text-red-500">*</span>
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setForm({
                                                    ...form,
                                                    brand_colors: [...form.brand_colors, "#000000"],
                                                })
                                            }
                                            className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
                                        >
                                            + Add Color
                                        </button>
                                    </div>
                                    <div className="space-y-3">
                                        {form.brand_colors.map((color, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                <input
                                                    type="color"
                                                    value={color}
                                                    onChange={(e) => {
                                                        const colors = [...form.brand_colors];
                                                        colors[index] = e.target.value;
                                                        setForm({ ...form, brand_colors: colors });
                                                        if (errors.brand_colors) {
                                                            setErrors({ ...errors, brand_colors: "" });
                                                        }
                                                    }}
                                                    className="h-12 w-14 rounded-lg border cursor-pointer"
                                                />
                                                <input
                                                    type="text"
                                                    value={color}
                                                    onChange={(e) => {
                                                        const colors = [...form.brand_colors];
                                                        colors[index] = e.target.value;
                                                        setForm({ ...form, brand_colors: colors });
                                                        if (errors.brand_colors) {
                                                            setErrors({ ...errors, brand_colors: "" });
                                                        }
                                                    }}
                                                    className={`flex-1 rounded-lg border px-4 py-2 ${
                                                        errors.brand_colors ? "border-red-500" : "border-gray-300"
                                                    }`}
                                                />
                                                {form.brand_colors.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const colors = form.brand_colors.filter(
                                                                (_, i) => i !== index
                                                            );
                                                            setForm({ ...form, brand_colors: colors });
                                                        }}
                                                        className="rounded-lg bg-red-500 px-3 py-2 text-white hover:bg-red-600"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        {errors.brand_colors && (
                                            <p className="text-red-500 text-sm">{errors.brand_colors}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Logo */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Logo</label>
                                    <input
                                        type="file"
                                        onChange={logo}
                                        accept="image/*"
                                        className="block mt-2"
                                    />
                                    {form.brand_logo && (
                                        <img
                                            src={form.brand_logo}
                                            alt="Brand logo preview"
                                            className="mt-3 w-20 h-20 rounded border object-cover"
                                        />
                                    )}
                                </div>

                                {/* Status */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700">
                                        Status <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="brand_status"
                                        value={form.brand_status}
                                        onChange={input}
                                        className={`w-full border rounded-lg p-2 mt-2 ${
                                            errors.brand_status ? "border-red-500" : "border-gray-300"
                                        }`}
                                    >
                                        <option value="ACTIVE">ACTIVE</option>
                                        <option value="INACTIVE">INACTIVE</option>
                                    </select>
                                    {errors.brand_status && (
                                        <p className="text-red-500 text-sm mt-1">{errors.brand_status}</p>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="border-t p-5 flex justify-end gap-3">
                                <button
                                    onClick={closeModal}
                                    className="border px-4 py-2 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={edit ? updateBrand : createBrand}
                                    className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : edit ? "Update Brand" : "Create Brand"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}